import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Phone, Mail, ArrowRight, Loader2, ChevronLeft,
  Wallet, MonitorPlay, Users, LifeBuoy, CreditCard, Network,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const INPUT_CLS = 'h-11 bg-white border-[#d8cfa8] text-[#3f4a17] placeholder:text-[#a79f78] focus-visible:ring-[#c9a227] focus-visible:border-transparent';
const GOLD_BTN = 'w-full h-11 bg-gradient-to-r from-[#d4af37] via-[#c9a227] to-[#a88a2a] hover:from-[#c9a227] hover:to-[#8f7422] text-white font-bold shadow-md';

const FEATURES = [
  { title: 'Wallet Top-ups', desc: 'Recharge OTT, IPTV & Railtel wallets.', icon: Wallet, color: 'text-amber-400' },
  { title: 'IPTV & OTT', desc: 'Activate, renew & check subscriptions.', icon: MonitorPlay, color: 'text-fuchsia-400' },
  { title: 'Manage LCOs', desc: 'Add resellers & set custom pricing.', icon: Users, color: 'text-sky-400' },
  { title: 'Payments', desc: 'Secure Easebuzz payments & ledger.', icon: CreditCard, color: 'text-emerald-400' },
  { title: 'Support', desc: 'Raise & track helpdesk tickets.', icon: LifeBuoy, color: 'text-rose-400' },
  { title: 'Subscribers', desc: 'Track & sync every connection.', icon: Network, color: 'text-violet-400' },
];

export default function PortalLogin() {
  const navigate = useNavigate();
  const [method, setMethod] = useState(null); // null = choose, 'whatsapp', 'email'
  const [step, setStep] = useState('input'); // input, otp
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const basePath = window.location.pathname.startsWith('/partner') ? '/partner' : '/portal';
  const isPartner = basePath === '/partner';
  const portalName = isPartner ? 'Distributor & LCO Portal' : 'Customer Portal';

  const getError = (data, fallback) => {
    if (!data) return fallback;
    if (typeof data.detail === 'string') return data.detail;
    if (Array.isArray(data.detail)) return data.detail.map(d => d.msg || d.message).filter(Boolean).join(', ') || fallback;
    if (data.message) return data.message;
    return fallback;
  };

  const handleSendWhatsAppOTP = async (e) => {
    e.preventDefault();
    if (!phone || phone.length !== 10) { toast.error('Please enter a valid 10-digit phone number'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/portal/auth/send-otp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone, portal_type: isPartner ? 'partner' : 'customer' })
      });
      const data = await res.json();
      if (res.ok) { toast.success('OTP sent to your WhatsApp!'); setStep('otp'); }
      else { toast.error(getError(data, 'Failed to send WhatsApp OTP. Please try again.')); }
    } catch { toast.error('Unable to connect to server. Please check your internet connection.'); }
    finally { setLoading(false); }
  };

  const handleSendEmailOTP = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) { toast.error('Please enter a valid email address'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/portal/auth/send-email-otp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, portal_type: isPartner ? 'partner' : 'customer' })
      });
      const data = await res.json();
      if (res.ok) { toast.success('OTP sent to your email!'); setStep('otp'); }
      else { toast.error(getError(data, 'Failed to send Email OTP. Please check your email or try WhatsApp.')); }
    } catch { toast.error('Unable to connect to server. Please check your internet connection.'); }
    finally { setLoading(false); }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!otp || otp.length !== 6) { toast.error('Please enter the 6-digit OTP'); return; }
    setLoading(true);
    try {
      const endpoint = method === 'whatsapp'
        ? `${API}/api/portal/auth/verify-otp`
        : `${API}/api/portal/auth/verify-email-otp`;
      const body = method === 'whatsapp'
        ? { phone, otp, portal_type: isPartner ? 'partner' : 'customer' }
        : { email, otp, portal_type: isPartner ? 'partner' : 'customer' };
      const res = await fetch(endpoint, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
      });
      const data = await res.json();
      if (res.ok) {
        localStorage.setItem('portal_token', data.token);
        localStorage.setItem('portal_user', JSON.stringify(data.user));
        toast.success('Login successful!');
        navigate(`${basePath}/dashboard`);
      } else { toast.error(getError(data, 'Invalid or expired OTP. Please try again.')); }
    } catch { toast.error('Unable to connect to server. Please check your internet connection.'); }
    finally { setLoading(false); }
  };

  const handleBack = () => {
    if (step === 'otp') { setStep('input'); setOtp(''); }
    else { setMethod(null); setPhone(''); setEmail(''); }
  };

  const subtitle = !method ? 'Choose your login method'
    : step === 'input'
      ? (method === 'whatsapp' ? 'Login with your registered WhatsApp number' : 'Login with your registered email')
      : `Enter the OTP sent to your ${method === 'whatsapp' ? 'WhatsApp' : 'email'}`;

  return (
    <div
      className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-2"
      style={{ background: 'linear-gradient(135deg,#3f4a17 0%,#5c6524 26%,#8a7d2c 50%,#c9a227 72%,#7d3f9c 100%)' }}
      data-testid="portal-login-page"
    >
      {/* Left: golden flower + portal description */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden">
        <img src="/golden-flower-bg.png" alt="Golden lotus" aria-hidden="true"
          className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg,rgba(45,53,20,0.82) 0%,rgba(63,74,23,0.6) 50%,rgba(98,45,120,0.4) 100%)' }} />

        <div className="relative z-10">
          <img src="/logo.png" alt="ANT Broadband" className="h-14 w-auto" />
        </div>

        <div className="relative z-10 max-w-lg">
          <p className="text-xs font-bold tracking-[0.25em] text-[#e9c76b] uppercase">{isPartner ? 'Partner & LCO' : 'Customer'} Portal</p>
          <h1 className="mt-3 text-4xl lg:text-5xl font-bold tracking-tight text-white drop-shadow">
            {isPartner ? 'Grow your distribution business.' : 'Manage your connections with ease.'}
          </h1>
          <p className="mt-4 text-base text-[#f3efdf] leading-relaxed">
            Recharge wallets, activate IPTV &amp; OTT, manage resellers and raise support tickets — everything you need,
            in one secure portal.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mt-10" data-testid="portal-features-grid">
            {FEATURES.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.title} className="flex flex-col gap-1.5">
                  <Icon className={`h-6 w-6 ${f.color}`} />
                  <p className="text-sm font-semibold text-white">{f.title}</p>
                  <p className="text-sm text-[#dcd6b8]">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>

        <p className="relative z-10 text-xs text-[#d8d0aa]">© {new Date().getFullYear()} ANT Broadband. All rights reserved.</p>
      </div>

      {/* Right: login card */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm rounded-2xl border border-[#e3dab7] bg-[#fbf8ef] shadow-2xl p-8 space-y-7" data-testid="portal-login-card">
          <div className="lg:hidden flex flex-col items-center gap-3">
            <img src="/logo.png" alt="ANT Broadband" className="h-14 w-auto" />
            <img src="/golden-flower-bg.png" alt="" className="h-16 w-16 rounded-full object-cover ring-2 ring-[#d4af37]" />
          </div>

          <div className="space-y-2 text-center lg:text-left">
            <p className="text-xs font-bold tracking-[0.22em] text-[#a88a2a] uppercase">{isPartner ? 'Partner / LCO' : 'Customer'}</p>
            <h2 className="text-2xl lg:text-3xl font-semibold tracking-tight text-[#3f4a17]">{portalName}</h2>
            <p className="text-sm text-[#7a744f]">{subtitle}</p>
          </div>

          {/* Method Selection */}
          {!method && (
            <div className="space-y-3">
              <Button className="w-full h-12 text-base bg-gradient-to-r from-[#6b7328] to-[#556b2f] hover:opacity-90 text-white"
                onClick={() => { setMethod('whatsapp'); setStep('input'); }} data-testid="login-whatsapp-btn">
                <Phone className="mr-3 h-5 w-5" /> Login with WhatsApp
              </Button>
              <Button variant="outline" className="w-full h-12 text-base border-[#c9a227] text-[#8f7422] hover:bg-[#f6efd8] hover:text-[#8f7422]"
                onClick={() => { setMethod('email'); setStep('input'); }} data-testid="login-email-btn">
                <Mail className="mr-3 h-5 w-5" /> Login with Email
              </Button>
            </div>
          )}

          {/* WhatsApp Phone Input */}
          {method === 'whatsapp' && step === 'input' && (
            <form onSubmit={handleSendWhatsAppOTP} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-[#5a5320]">WhatsApp Number</Label>
                <div className="flex gap-2">
                  <div className="flex items-center px-3 bg-[#efe9d2] border border-[#d8cfa8] rounded-md text-[#5a5320] text-sm">+91</div>
                  <Input id="phone" type="tel" placeholder="9876543210" value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    className={`flex-1 ${INPUT_CLS}`} autoFocus data-testid="phone-input" />
                </div>
                <p className="text-xs text-[#8a8256]">We'll send a one-time password to this number</p>
              </div>
              <Button type="submit" className={GOLD_BTN} disabled={loading} data-testid="send-whatsapp-otp-btn">
                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending OTP...</> : <><Phone className="mr-2 h-4 w-4" /> Send OTP</>}
              </Button>
              <Button type="button" variant="ghost" className="w-full text-[#7a744f]" onClick={handleBack}>
                <ChevronLeft className="mr-1 h-4 w-4" /> Back
              </Button>
            </form>
          )}

          {/* Email Input */}
          {method === 'email' && step === 'input' && (
            <form onSubmit={handleSendEmailOTP} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#5a5320]">Registered Email</Label>
                <Input id="email" type="email" placeholder="customer@example.com" value={email}
                  onChange={(e) => setEmail(e.target.value)} autoFocus className={INPUT_CLS} data-testid="email-input" />
                <p className="text-xs text-[#8a8256]">We'll send a one-time password to this email</p>
              </div>
              <Button type="submit" className={GOLD_BTN} disabled={loading} data-testid="send-email-otp-btn">
                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending OTP...</> : <><Mail className="mr-2 h-4 w-4" /> Send OTP</>}
              </Button>
              <Button type="button" variant="ghost" className="w-full text-[#7a744f]" onClick={handleBack}>
                <ChevronLeft className="mr-1 h-4 w-4" /> Back
              </Button>
            </form>
          )}

          {/* OTP Verification */}
          {step === 'otp' && (
            <form onSubmit={handleVerifyOTP} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="otp" className="text-[#5a5320]">Enter OTP</Label>
                <Input id="otp" type="text" placeholder="••••••" value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className={`text-center text-2xl tracking-[0.5em] h-14 ${INPUT_CLS}`} maxLength={6} autoFocus data-testid="otp-input" />
                <p className="text-xs text-[#8a8256]">OTP sent to {method === 'whatsapp' ? `+91 ${phone}` : email}</p>
              </div>
              <Button type="submit" className={GOLD_BTN} disabled={loading} data-testid="verify-otp-btn">
                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Verifying...</> : <><ArrowRight className="mr-2 h-4 w-4" /> Login</>}
              </Button>
              <Button type="button" variant="ghost" className="w-full text-[#7a744f]" onClick={handleBack}>
                <ChevronLeft className="mr-1 h-4 w-4" /> Back
              </Button>
            </form>
          )}

          <div className="pt-2 border-t border-[#e3dab7] text-center">
            <p className="text-xs text-[#7a744f] mt-3">
              New partner?{' '}
              <button type="button" className="text-[#7d3f9c] hover:underline font-semibold" onClick={() => navigate(`${basePath}/signup`)} data-testid="signup-link">
                Register here
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
